import { type DragEvent, useState } from "react";
import type { AssetResponse } from "../../generated/api/types.gen";
import { startAssetDrag } from "./galleryTree";

/**
 * Project-asset cards carry ``used`` (derived from whether a design uses the
 * asset); the global "My Assets"/org views pass plain ``AssetResponse`` with no
 * ``used`` field, which reads as ``undefined`` → solid styling.
 */
type AssetCard = AssetResponse & { used?: boolean };

interface GalleryAssetGridProps {
  assets: AssetCard[];
  /** Empty-state copy varies by scope ("My Assets" vs the org bucket). */
  emptyMessage?: string;
  /** Render a single column (the project assets sidebar) vs the responsive grid. */
  singleColumn?: boolean;
  /**
   * Whether cards may be dragged (to share a personal asset into the org).
   * The project assets sidebar passes ``false`` since there's nowhere to drop.
   */
  draggable?: boolean;
}

/**
 * Image-asset thumbnail with a pulsing skeleton until the file loads. Color
 * chips paint instantly, so without this the image cards sit empty for a
 * moment while the S3 file downloads. ``className`` positions and sizes the
 * frame; the default is the gallery card's fixed-width chip.
 */
export function AssetThumbnailImage({
  src,
  className = "relative aspect-84/80 w-[clamp(64px,26%,84px)] shrink-0",
}: {
  src: string;
  className?: string;
}) {
  const [status, setStatus] = useState<"loading" | "loaded" | "error">(
    "loading",
  );
  return (
    <span aria-hidden="true" className={`block ${className}`}>
      {status !== "loaded" && (
        <span
          data-testid="asset-thumb-skeleton"
          className={`absolute inset-0 bg-[var(--bg-field)] ${
            status === "loading" ? "animate-pulse" : ""
          }`}
        />
      )}
      <img
        alt=""
        src={src}
        onLoad={() => setStatus("loaded")}
        onError={() => setStatus("error")}
        className={`block h-full w-full object-cover ${
          status === "loaded" ? "opacity-100" : "opacity-0"
        }`}
      />
    </span>
  );
}

function GalleryAssetCard({
  asset,
  draggable,
}: {
  asset: AssetCard;
  draggable: boolean;
}) {
  const isShared = asset.org_id !== null;
  const isCatalog = asset.source === "manufacturer";
  // Unused = attached to the project but no design references it yet. Rendered
  // greyed-out with a dashed border to set it apart from used assets.
  const isUnused = asset.used === false;
  // Personal cards drag → can be shared into the org section. Org-shared
  // cards are not draggable: the ticket rule is "users should not be
  // able drag assets out of an org's asset folder". The sidebar disables
  // dragging entirely via the ``draggable`` prop. Saved catalog items aren't
  // the user's to share, so they don't drag either.
  const isDraggable = draggable && !isShared && !isCatalog;
  const onDragStart = isDraggable
    ? (event: DragEvent<HTMLLIElement>) => startAssetDrag(asset, event)
    : undefined;

  return (
    <li
      draggable={isDraggable}
      onDragStart={onDragStart}
      aria-label={asset.name}
      data-used={isUnused ? "false" : "true"}
      className={`flex overflow-hidden rounded-lg ${
        isUnused
          ? "border border-dashed border-[var(--stroke-field)] opacity-60"
          : "shadow-sm"
      } ${isDraggable ? "cursor-grab active:cursor-grabbing" : ""}`}
    >
      {asset.type === "image" ? (
        <AssetThumbnailImage src={asset.value} />
      ) : (
        <span
          aria-hidden="true"
          className="block aspect-84/80 w-[clamp(64px,26%,84px)] shrink-0"
          style={{ backgroundColor: asset.value }}
        />
      )}
      <div className="flex min-w-0 flex-1 items-center self-stretch rounded-r-lg bg-white py-2 pl-3 pr-2">
        <span className="min-w-0 truncate text-[15px] font-semibold leading-5 text-[#20262C]">
          {asset.name}
        </span>
      </div>
    </li>
  );
}

export function GalleryAssetGrid({
  assets,
  emptyMessage = "No saved assets yet. Add one to see it here.",
  singleColumn = false,
  draggable = true,
}: GalleryAssetGridProps) {
  if (assets.length === 0) {
    return (
      <p className="text-sm text-[var(--text-secondary)]">{emptyMessage}</p>
    );
  }

  return (
    <ul
      className={`grid list-none gap-5 p-0 ${
        singleColumn
          ? "grid-cols-1"
          : "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
      }`}
    >
      {assets.map((asset) => (
        <GalleryAssetCard key={asset.id} asset={asset} draggable={draggable} />
      ))}
    </ul>
  );
}
